import React, { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { ArrowLeft, Home, FileText, Clock, Calendar, Mail, ChefHat, Sofa, PaintBucket, Lightbulb, Bath, Hammer, TreePine, Download, Send, CheckCircle, Loader2, X, DollarSign, Package, FileDown, Eye } from 'lucide-react';
type CustomerSelection = {
  id: string;
  category: string;
  optionName: string;
  description: string;
  price: number;
  selectedAt: string;
};
// Mock customer data
const customersData: Record<string, {
  id: string;
  type: 'lead' | 'buyer';
  status: 'active' | 'invited' | 'inactive';
  email?: string;
  lastLogin: string;
  age: string;
  timeSpent: string;
  model: string;
  modelImage: string;
  modelSpecs: string;
  projectName: string;
  purchaseDate?: string;
  contractValue?: number;
  selections: CustomerSelection[];
}> = {
  'User-8823': {
    id: 'User-8823',
    type: 'buyer',
    status: 'active',
    email: 'john.doe@example.com',
    lastLogin: '10 mins ago',
    age: '3 days',
    timeSpent: '2h 15m',
    model: 'The Lawson',
    modelImage: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=400&q=80',
    modelSpecs: '1795 sq ft • 3 beds • 2.5 baths',
    projectName: 'Oakwood Estates',
    purchaseDate: '2024-01-15',
    contractValue: 650000,
    selections: [{
      id: 's1',
      category: 'Kitchen',
      optionName: 'Quartz Countertops',
      description: 'Premium white quartz with subtle veining',
      price: 4500,
      selectedAt: '2024-01-10'
    }, {
      id: 's2',
      category: 'Kitchen',
      optionName: 'Stainless Steel Appliance Package',
      description: 'Samsung 4-piece stainless steel set',
      price: 8200,
      selectedAt: '2024-01-10'
    }, {
      id: 's3',
      category: 'Kitchen',
      optionName: 'Soft-Close Cabinets',
      description: 'Upgraded cabinet hardware throughout',
      price: 1200,
      selectedAt: '2024-01-11'
    }, {
      id: 's4',
      category: 'Flooring',
      optionName: 'Engineered Hardwood',
      description: 'Oak finish, main floor throughout',
      price: 6800,
      selectedAt: '2024-01-11'
    }, {
      id: 's5',
      category: 'Flooring',
      optionName: 'Heated Bathroom Floors',
      description: 'Radiant heating in master bath',
      price: 2400,
      selectedAt: '2024-01-12'
    }, {
      id: 's6',
      category: 'Exterior',
      optionName: 'Stone Accent Facade',
      description: 'Natural stone on front elevation',
      price: 5500,
      selectedAt: '2024-01-12'
    }, {
      id: 's7',
      category: 'Smart Home',
      optionName: 'Smart Thermostat Package',
      description: 'Nest thermostat with zone control',
      price: 1800,
      selectedAt: '2024-01-13'
    }, {
      id: 's8',
      category: 'Smart Home',
      optionName: 'Security System',
      description: 'Ring doorbell + 4 cameras',
      price: 2200,
      selectedAt: '2024-01-13'
    }, {
      id: 's9',
      category: 'Bathroom',
      optionName: 'Freestanding Soaker Tub',
      description: 'Modern oval design, master bath',
      price: 3200,
      selectedAt: '2024-01-14'
    }]
  },
  'User-7732': {
    id: 'User-7732',
    type: 'lead',
    status: 'active',
    lastLogin: '1 hour ago',
    age: '5 days',
    timeSpent: '4h 30m',
    model: 'The Lawson',
    modelImage: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=400&q=80',
    modelSpecs: '1795 sq ft • 3 beds • 2.5 baths',
    projectName: 'Oakwood Estates',
    selections: [{
      id: 's1',
      category: 'Kitchen',
      optionName: 'Granite Countertops',
      description: 'Black galaxy granite',
      price: 3800,
      selectedAt: '2024-01-18'
    }, {
      id: 's2',
      category: 'Kitchen',
      optionName: 'Gas Range Upgrade',
      description: '6-burner professional range',
      price: 4200,
      selectedAt: '2024-01-18'
    }, {
      id: 's3',
      category: 'Flooring',
      optionName: 'Luxury Vinyl Plank',
      description: 'Waterproof LVP, full main floor',
      price: 4500,
      selectedAt: '2024-01-19'
    }, {
      id: 's4',
      category: 'Exterior',
      optionName: 'Extended Patio',
      description: 'Additional 200 sq ft paver patio',
      price: 6200,
      selectedAt: '2024-01-19'
    }, {
      id: 's5',
      category: 'Smart Home',
      optionName: 'Whole Home Audio',
      description: 'Sonos built-in speakers, 6 zones',
      price: 5800,
      selectedAt: '2024-01-20'
    }]
  },
  'User-5519': {
    id: 'User-5519',
    type: 'buyer',
    status: 'active',
    email: 'jane.smith@example.com',
    lastLogin: 'Just now',
    age: '1 week',
    timeSpent: '1h 20m',
    model: 'The Lawson',
    modelImage: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=400&q=80',
    modelSpecs: '1795 sq ft • 3 beds • 2.5 baths',
    projectName: 'Oakwood Estates',
    purchaseDate: '2024-01-18',
    contractValue: 680000,
    selections: [{
      id: 's1',
      category: 'Kitchen',
      optionName: 'Quartz Countertops',
      description: 'Calacatta gold pattern',
      price: 5200,
      selectedAt: '2024-01-15'
    }, {
      id: 's2',
      category: 'Flooring',
      optionName: 'Wide Plank Hardwood',
      description: 'White oak, 7" planks',
      price: 9200,
      selectedAt: '2024-01-15'
    }, {
      id: 's3',
      category: 'Bathroom',
      optionName: 'Dual Vanity Upgrade',
      description: 'Extended 72" double vanity',
      price: 2800,
      selectedAt: '2024-01-16'
    }]
  },
  'User-6621': {
    id: 'User-6621',
    type: 'lead',
    status: 'inactive',
    lastLogin: '5 days ago',
    age: '2 weeks',
    timeSpent: '45m',
    model: 'The Samson',
    modelImage: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=400&q=80',
    modelSpecs: '2235 sq ft • 4 beds • 2.5 baths',
    projectName: 'Maple Residences',
    selections: [{
      id: 's1',
      category: 'Structural',
      optionName: 'Basement Development',
      description: 'Full basement finish with rec room',
      price: 45000,
      selectedAt: '2024-01-05'
    }, {
      id: 's2',
      category: 'Kitchen',
      optionName: 'Butler Pantry',
      description: 'Walk-in pantry with prep sink',
      price: 8500,
      selectedAt: '2024-01-06'
    }]
  },
  'User-4408': {
    id: 'User-4408',
    type: 'lead',
    status: 'active',
    lastLogin: '30 mins ago',
    age: '2 days',
    timeSpent: '3h 45m',
    model: 'The Samson',
    modelImage: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=400&q=80',
    modelSpecs: '2235 sq ft • 4 beds • 2.5 baths',
    projectName: 'Maple Residences',
    selections: [{
      id: 's1',
      category: 'Kitchen',
      optionName: 'Premium Appliance Package',
      description: 'Wolf/Sub-Zero suite',
      price: 18500,
      selectedAt: '2024-01-20'
    }, {
      id: 's2',
      category: 'Kitchen',
      optionName: 'Custom Cabinetry',
      description: 'Floor-to-ceiling shaker style',
      price: 12000,
      selectedAt: '2024-01-20'
    }, {
      id: 's3',
      category: 'Flooring',
      optionName: 'Heated Floors Package',
      description: 'All bathrooms + mudroom',
      price: 4800,
      selectedAt: '2024-01-21'
    }, {
      id: 's4',
      category: 'Exterior',
      optionName: 'Three-Car Garage',
      description: 'Extended garage with workshop',
      price: 35000,
      selectedAt: '2024-01-21'
    }, {
      id: 's5',
      category: 'Smart Home',
      optionName: 'Full Automation Package',
      description: 'Control4 whole-home system',
      price: 22000,
      selectedAt: '2024-01-22'
    }, {
      id: 's6',
      category: 'Bathroom',
      optionName: 'Steam Shower',
      description: 'Master bath steam system',
      price: 6500,
      selectedAt: '2024-01-22'
    }, {
      id: 's7',
      category: 'Structural',
      optionName: 'Covered Deck',
      description: '400 sq ft covered outdoor living',
      price: 28000,
      selectedAt: '2024-01-22'
    }]
  },
  'User-3397': {
    id: 'User-3397',
    type: 'buyer',
    status: 'active',
    email: 'mike.johnson@example.com',
    lastLogin: '2 hours ago',
    age: '1 month',
    timeSpent: '5h 10m',
    model: 'The Lawson',
    modelImage: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=400&q=80',
    modelSpecs: '1795 sq ft • 3 beds • 2.5 baths',
    projectName: 'Oakwood Estates',
    purchaseDate: '2024-01-10',
    contractValue: 625000,
    selections: [{
      id: 's1',
      category: 'Kitchen',
      optionName: 'Quartz Countertops',
      description: 'Carrara style white quartz',
      price: 4500,
      selectedAt: '2024-01-08'
    }, {
      id: 's2',
      category: 'Flooring',
      optionName: 'Engineered Hardwood',
      description: 'Walnut finish throughout',
      price: 7200,
      selectedAt: '2024-01-08'
    }, {
      id: 's3',
      category: 'Smart Home',
      optionName: 'Smart Lock Package',
      description: 'Keyless entry all doors',
      price: 1800,
      selectedAt: '2024-01-09'
    }]
  }
};
const categoryIcons: Record<string, React.ElementType> = {
  Kitchen: ChefHat,
  Flooring: Sofa,
  Exterior: TreePine,
  'Smart Home': Lightbulb,
  Bathroom: Bath,
  Structural: Hammer
};
const categoryColors: Record<string, string> = {
  Kitchen: 'bg-orange-50 text-orange-600',
  Flooring: 'bg-amber-50 text-amber-600',
  Exterior: 'bg-green-50 text-green-600',
  'Smart Home': 'bg-blue-50 text-blue-600',
  Bathroom: 'bg-cyan-50 text-cyan-600',
  Structural: 'bg-slate-100 text-slate-600'
};
export function CustomerDetailPage() {
  const {
    id,
    builderId
  } = useParams<{
    id: string;
    builderId: string;
  }>();
  const navigate = useNavigate();
  // Download PDF modal state
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState<'generating' | 'ready'>('generating');
  // Send Summary modal state
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendStatus, setSendStatus] = useState<'compose' | 'sending' | 'success'>('compose');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const customer = id ? customersData[id] : null;
  // Group selections by category
  const groupedSelections = useMemo(() => {
    if (!customer) return {};
    return customer.selections.reduce((acc, selection) => {
      if (!acc[selection.category]) {
        acc[selection.category] = [];
      }
      acc[selection.category].push(selection);
      return acc;
    }, {} as Record<string, CustomerSelection[]>);
  }, [customer]);
  // Calculate total upgrade value
  const totalUpgradeValue = useMemo(() => {
    if (!customer) return 0;
    return customer.selections.reduce((sum, s) => sum + s.price, 0);
  }, [customer]);
  // Initialize email template when customer changes
  const getDefaultEmailTemplate = () => {
    if (!customer) return {
      subject: '',
      body: ''
    };
    return {
      subject: `Your ${customer.model} Selection Summary - ${customer.projectName}`,
      body: `Hi there,

Thank you for exploring your options for ${customer.model} at ${customer.projectName}!

We've put together a summary of the upgrades you've selected so far. Your current selections total $${totalUpgradeValue.toLocaleString()} in upgrades.

Please find your detailed selection summary attached to this email. It includes all the options you've chosen, organized by category, along with pricing details.

If you have any questions about your selections or would like to make changes, please don't hesitate to reach out. We're here to help make your new home exactly what you've envisioned.

Best regards,
The Mattamy Homes Team`
    };
  };
  const handleDownloadPDF = () => {
    setShowDownloadModal(true);
    setDownloadStatus('generating');
    setTimeout(() => {
      setDownloadStatus('ready');
    }, 2000);
  };
  const handleActualDownload = () => {
    // Simulate download
    console.log('Downloading PDF for', customer?.id);
    setShowDownloadModal(false);
    setDownloadStatus('generating');
  };
  const handleOpenSendModal = () => {
    const template = getDefaultEmailTemplate();
    setEmailSubject(template.subject);
    setEmailBody(template.body);
    setSendStatus('compose');
    setShowSendModal(true);
  };
  const handleSendEmail = () => {
    setSendStatus('sending');
    setTimeout(() => {
      setSendStatus('success');
    }, 1500);
  };
  const handleCloseSendModal = () => {
    setShowSendModal(false);
    setSendStatus('compose');
  };
  if (!customer) {
    return <div className="max-w-7xl mx-auto" data-id="element-4559">
        <div className="text-center py-12" data-id="element-4560">
          <p className="text-slate-500" data-id="element-4561">Customer not found</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate(`/${builderId}/customers`)} data-id="element-4562">
            Back to Customers
          </Button>
        </div>
      </div>;
  }
  return <>
      <div className="max-w-7xl mx-auto pb-24" data-id="element-4563">
        {/* Header */}
        <div className="mb-6" data-id="element-4564">
          <button onClick={() => navigate(`/${builderId}/customers`)} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors mb-4" data-id="element-4565">
            <ArrowLeft className="h-4 w-4" data-id="element-4566" />
            <span className="text-sm" data-id="element-4567">Back to Customers</span>
          </button>

          <div className="flex items-start justify-between" data-id="element-4568">
            <div data-id="element-4569">
              <div className="flex items-center gap-3 mb-2" data-id="element-4570">
                <h1 className="text-2xl font-bold text-slate-900" data-id="element-4571">
                  {customer.id}
                </h1>
                <Badge variant={customer.type === 'buyer' ? 'success' : 'primary'} data-id="element-4572">
                  {customer.type === 'buyer' ? 'Buyer' : 'Lead'}
                </Badge>
                <Badge variant={customer.status === 'active' ? 'success' : customer.status === 'invited' ? 'warning' : 'neutral'} data-id="element-4573">
                  {customer.status.charAt(0).toUpperCase() + customer.status.slice(1)}
                </Badge>
              </div>
              <div className="flex items-center gap-4 text-sm text-slate-500" data-id="element-4574">
                <span className="flex items-center gap-1" data-id="element-4575">
                  <Clock className="h-4 w-4" data-id="element-4576" />
                  Last active: {customer.lastLogin}
                </span>
                <span className="flex items-center gap-1" data-id="element-4577">
                  <Calendar className="h-4 w-4" data-id="element-4578" />
                  Customer for {customer.age}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Model Card */}
        <Card className="mb-6" data-id="element-4579">
          <div className="flex items-center gap-6 p-6" data-id="element-4580">
            <div className="h-24 w-32 rounded-lg overflow-hidden flex-shrink-0" data-id="element-4581">
              <img src={customer.modelImage} alt={customer.model} className="h-full w-full object-cover" data-id="element-4582" />
            </div>
            <div className="flex-1" data-id="element-4583">
              <div className="flex items-center gap-2 mb-1" data-id="element-4584">
                <Home className="h-5 w-5 text-primary-600" data-id="element-4585" />
                <h2 className="text-xl font-bold text-slate-900" data-id="element-4586">
                  {customer.model}
                </h2>
              </div>
              <p className="text-slate-600 mb-2" data-id="element-4587">{customer.modelSpecs}</p>
              <p className="text-sm text-slate-500" data-id="element-4588">{customer.projectName}</p>
            </div>
            {customer.contractValue && <div className="text-right" data-id="element-4589">
                <p className="text-sm text-slate-500" data-id="element-4590">Contract Value</p>
                <p className="text-2xl font-bold text-slate-900" data-id="element-4591">
                  ${customer.contractValue.toLocaleString()}
                </p>
              </div>}
          </div>
        </Card>

        {/* Selections Header */}
        <div className="flex items-center justify-between mb-4" data-id="element-4592">
          <div data-id="element-4593">
            <h2 className="text-lg font-semibold text-slate-900" data-id="element-4594">
              Selected Options
            </h2>
            <p className="text-sm text-slate-500" data-id="element-4595">
              {customer.selections.length} upgrades selected
            </p>
          </div>
        </div>

        {/* Grouped Selections */}
        <div className="space-y-6" data-id="element-4596">
          {Object.entries(groupedSelections).map(([category, selections]) => {
          const Icon = categoryIcons[category] || Package;
          const colorClass = categoryColors[category] || 'bg-slate-100 text-slate-600';
          const categoryTotal = selections.reduce((sum, s) => sum + s.price, 0);
          return <Card key={category} noPadding data-id="element-4597">
                <div className="p-4 border-b border-slate-100 flex items-center justify-between" data-id="element-4598">
                  <div className="flex items-center gap-3" data-id="element-4599">
                    <div className={`p-2 rounded-lg ${colorClass}`} data-id="element-4600">
                      <Icon className="h-5 w-5" data-id="element-4601" />
                    </div>
                    <div data-id="element-4602">
                      <h3 className="font-semibold text-slate-900" data-id="element-4603">
                        {category}
                      </h3>
                      <p className="text-sm text-slate-500" data-id="element-4604">
                        {selections.length} items
                      </p>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-4605">
                    <p className="text-sm text-slate-500" data-id="element-4606">Subtotal</p>
                    <p className="font-semibold text-slate-900" data-id="element-4607">
                      ${categoryTotal.toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="divide-y divide-slate-50" data-id="element-4608">
                  {selections.map(selection => <div key={selection.id} className="p-4 flex items-center justify-between" data-id="element-4609">
                      <div className="flex-1" data-id="element-4610">
                        <p className="font-medium text-slate-900" data-id="element-4611">
                          {selection.optionName}
                        </p>
                        <p className="text-sm text-slate-500" data-id="element-4612">
                          {selection.description}
                        </p>
                      </div>
                      <p className="text-lg font-semibold text-green-600 ml-4" data-id="element-4613">
                        ${selection.price.toLocaleString()}
                      </p>
                    </div>)}
                </div>
              </Card>;
        })}
        </div>

        {/* Empty State */}
        {customer.selections.length === 0 && <Card className="text-center py-12" data-id="element-4614">
            <Package className="h-12 w-12 text-slate-300 mx-auto mb-4" data-id="element-4615" />
            <p className="text-slate-500" data-id="element-4616">No selections yet</p>
            <p className="text-sm text-slate-400 mt-1" data-id="element-4617">
              This customer hasn't selected any upgrades
            </p>
          </Card>}
      </div>

      {/* Sticky Footer */}
      <div className="fixed bottom-0 left-64 right-0 bg-white border-t border-slate-200 p-4 z-40" data-id="element-4618">
        <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-4619">
          <div data-id="element-4620">
            <p className="text-sm text-slate-500" data-id="element-4621">Total Upgrade Value</p>
            <p className="text-3xl font-bold text-slate-900" data-id="element-4622">
              ${totalUpgradeValue.toLocaleString()}
            </p>
          </div>
          <div className="flex items-center gap-3" data-id="element-4623">
            <Button variant="outline" leftIcon={<Download className="h-4 w-4" data-id="element-4625" />} onClick={handleDownloadPDF} data-id="element-4624">
              Download PDF
            </Button>
            <Button leftIcon={<Send className="h-4 w-4" data-id="element-4627" />} onClick={handleOpenSendModal} data-id="element-4626">
              Send Summary to Customer
            </Button>
          </div>
        </div>
      </div>

      {/* Download PDF Modal */}
      {showDownloadModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-4628">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" data-id="element-4629">
            {downloadStatus === 'generating' ? <div className="p-8 text-center" data-id="element-4630">
                <div className="mx-auto w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mb-6 relative" data-id="element-4631">
                  <FileText className="h-10 w-10 text-slate-400" data-id="element-4632" />
                  <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center" data-id="element-4633">
                    <Loader2 className="h-4 w-4 text-white animate-spin" data-id="element-4634" />
                  </div>
                </div>
                <h2 className="text-xl font-bold text-slate-900 mb-2" data-id="element-4635">
                  Generating PDF
                </h2>
                <p className="text-slate-500 text-sm mb-6" data-id="element-4636">
                  Creating your selection summary document...
                </p>
                <div className="space-y-3 text-left max-w-xs mx-auto" data-id="element-4637">
                  <div className="flex items-center gap-3 text-sm" data-id="element-4638">
                    <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" data-id="element-4639" />
                    <span className="text-slate-600" data-id="element-4640">Gathering selections</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm" data-id="element-4641">
                    <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" data-id="element-4642" />
                    <span className="text-slate-600" data-id="element-4643">Calculating totals</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm" data-id="element-4644">
                    <Loader2 className="h-4 w-4 text-primary-500 animate-spin flex-shrink-0" data-id="element-4645" />
                    <span className="text-slate-600" data-id="element-4646">Formatting document</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm" data-id="element-4647">
                    <div className="h-4 w-4 rounded-full border-2 border-slate-300 flex-shrink-0" data-id="element-4648" />
                    <span className="text-slate-400" data-id="element-4649">Preparing download</span>
                  </div>
                </div>
              </div> : <>
                <div className="p-6 border-b border-slate-200 flex items-center justify-between" data-id="element-4650">
                  <h2 className="text-xl font-bold text-slate-900" data-id="element-4651">
                    PDF Ready
                  </h2>
                  <button onClick={() => setShowDownloadModal(false)} className="text-slate-400 hover:text-slate-600 transition-colors" data-id="element-4652">
                    <X className="h-5 w-5" data-id="element-4653" />
                  </button>
                </div>
                <div className="p-6" data-id="element-4654">
                  <div className="mx-auto w-20 h-20 bg-green-50 rounded-2xl flex items-center justify-center mb-6 relative" data-id="element-4655">
                    <FileText className="h-10 w-10 text-green-600" data-id="element-4656" />
                    <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center" data-id="element-4657">
                      <CheckCircle className="h-4 w-4 text-white" data-id="element-4658" />
                    </div>
                  </div>

                  {/* PDF Preview Card */}
                  <div className="bg-slate-50 rounded-lg p-4 mb-4" data-id="element-4659">
                    <div className="flex items-start gap-4" data-id="element-4660">
                      <div className="w-12 h-16 bg-white rounded border border-slate-200 flex items-center justify-center flex-shrink-0" data-id="element-4661">
                        <FileText className="h-6 w-6 text-red-500" data-id="element-4662" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-4663">
                        <p className="font-medium text-slate-900 truncate" data-id="element-4664">
                          {customer?.id}_Selection_Summary.pdf
                        </p>
                        <p className="text-sm text-slate-500 mt-1" data-id="element-4665">
                          {customer?.selections.length} items • $
                          {totalUpgradeValue.toLocaleString()} total
                        </p>
                        <p className="text-xs text-slate-400 mt-1" data-id="element-4666">
                          Generated just now
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm" data-id="element-4667">
                    <div className="flex justify-between" data-id="element-4668">
                      <span className="text-slate-600" data-id="element-4669">Customer:</span>
                      <span className="font-medium text-slate-900" data-id="element-4670">
                        {customer?.id}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-4671">
                      <span className="text-slate-600" data-id="element-4672">Model:</span>
                      <span className="font-medium text-slate-900" data-id="element-4673">
                        {customer?.model}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-4674">
                      <span className="text-slate-600" data-id="element-4675">Total Upgrades:</span>
                      <span className="font-medium text-green-600" data-id="element-4676">
                        ${totalUpgradeValue.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="p-6 border-t border-slate-200 flex justify-end gap-3" data-id="element-4677">
                  <Button variant="outline" onClick={() => setShowDownloadModal(false)} data-id="element-4678">
                    Cancel
                  </Button>
                  <Button leftIcon={<FileDown className="h-4 w-4" data-id="element-4680" />} onClick={handleActualDownload} data-id="element-4679">
                    Download PDF
                  </Button>
                </div>
              </>}
          </div>
        </div>}

      {/* Send Summary Modal */}
      {showSendModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-4681">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col" data-id="element-4682">
            {sendStatus === 'compose' ? <>
                <div className="p-6 border-b border-slate-200 flex items-center justify-between flex-shrink-0" data-id="element-4683">
                  <div data-id="element-4684">
                    <h2 className="text-xl font-bold text-slate-900" data-id="element-4685">
                      Send Selection Summary
                    </h2>
                    <p className="text-sm text-slate-500 mt-1" data-id="element-4686">
                      Customize the email before sending to the customer
                    </p>
                  </div>
                  <button onClick={handleCloseSendModal} className="text-slate-400 hover:text-slate-600 transition-colors" data-id="element-4687">
                    <X className="h-5 w-5" data-id="element-4688" />
                  </button>
                </div>

                <div className="p-6 space-y-4 overflow-y-auto flex-1" data-id="element-4689">
                  {/* Recipient Info */}
                  <div className="bg-slate-50 rounded-lg p-4 flex items-center gap-4" data-id="element-4690">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-4691">
                      <Mail className="h-5 w-5 text-primary-600" data-id="element-4692" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-4693">
                      <p className="text-sm text-slate-500" data-id="element-4694">Sending to</p>
                      <p className="font-medium text-slate-900" data-id="element-4695">
                        {customer?.id}
                      </p>
                    </div>
                    <Badge variant="success" data-id="element-4696">
                      <div className="flex items-center gap-1" data-id="element-4697">
                        <FileText className="h-3 w-3" data-id="element-4698" />
                        PDF Attached
                      </div>
                    </Badge>
                  </div>

                  {/* Email Subject */}
                  <div data-id="element-4699">
                    <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4700">
                      Subject Line
                    </label>
                    <input type="text" value={emailSubject} onChange={e => setEmailSubject(e.target.value)} className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" data-id="element-4701" />
                  </div>

                  {/* Email Body */}
                  <div data-id="element-4702">
                    <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4703">
                      Message
                    </label>
                    <textarea value={emailBody} onChange={e => setEmailBody(e.target.value)} rows={12} className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm leading-relaxed" data-id="element-4704" />
                  </div>

                  {/* Attachment Preview */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-4705">
                    <p className="text-sm font-medium text-blue-900 mb-2" data-id="element-4706">
                      Attachment
                    </p>
                    <div className="flex items-center gap-3" data-id="element-4707">
                      <div className="w-10 h-12 bg-white rounded border border-blue-200 flex items-center justify-center flex-shrink-0" data-id="element-4708">
                        <FileText className="h-5 w-5 text-red-500" data-id="element-4709" />
                      </div>
                      <div data-id="element-4710">
                        <p className="text-sm font-medium text-blue-900" data-id="element-4711">
                          {customer?.id}_Selection_Summary.pdf
                        </p>
                        <p className="text-xs text-blue-700" data-id="element-4712">
                          {customer?.selections.length} items • $
                          {totalUpgradeValue.toLocaleString()} total
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg flex items-start gap-2" data-id="element-4713">
                    <Mail className="h-4 w-4 text-slate-500 mt-0.5 flex-shrink-0" data-id="element-4714" />
                    <p className="text-xs text-slate-600" data-id="element-4715">
                      The customer's email address is kept private. This email
                      will be sent directly to them without exposing their
                      contact information.
                    </p>
                  </div>
                </div>

                <div className="p-6 border-t border-slate-200 flex justify-between items-center flex-shrink-0 bg-slate-50" data-id="element-4716">
                  <Button variant="ghost" onClick={() => {
              const template = getDefaultEmailTemplate();
              setEmailSubject(template.subject);
              setEmailBody(template.body);
            }} data-id="element-4717">
                    Reset to Default
                  </Button>
                  <div className="flex gap-3" data-id="element-4718">
                    <Button variant="outline" onClick={handleCloseSendModal} data-id="element-4719">
                      Cancel
                    </Button>
                    <Button leftIcon={<Send className="h-4 w-4" data-id="element-4721" />} onClick={handleSendEmail} data-id="element-4720">
                      Send Email
                    </Button>
                  </div>
                </div>
              </> : sendStatus === 'sending' ? <div className="p-8 text-center" data-id="element-4722">
                <div className="mx-auto w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mb-4" data-id="element-4723">
                  <Loader2 className="h-8 w-8 text-primary-600 animate-spin" data-id="element-4724" />
                </div>
                <h2 className="text-xl font-bold text-slate-900 mb-2" data-id="element-4725">
                  Sending Email
                </h2>
                <p className="text-slate-500 text-sm" data-id="element-4726">
                  Delivering your message to {customer?.id}...
                </p>
              </div> : <>
                <div className="p-6 border-b border-slate-200 flex items-center justify-between" data-id="element-4727">
                  <h2 className="text-xl font-bold text-slate-900" data-id="element-4728">
                    Email Sent Successfully
                  </h2>
                  <button onClick={handleCloseSendModal} className="text-slate-400 hover:text-slate-600 transition-colors" data-id="element-4729">
                    <X className="h-5 w-5" data-id="element-4730" />
                  </button>
                </div>
                <div className="p-6" data-id="element-4731">
                  <div className="mx-auto w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4" data-id="element-4732">
                    <CheckCircle className="h-8 w-8 text-green-600" data-id="element-4733" />
                  </div>
                  <p className="text-slate-500 text-sm mb-6 text-center" data-id="element-4734">
                    Your selection summary has been sent to the customer.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm" data-id="element-4735">
                    <div className="flex justify-between" data-id="element-4736">
                      <span className="text-slate-600" data-id="element-4737">Sent to:</span>
                      <span className="font-medium text-slate-900" data-id="element-4738">
                        {customer?.id}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-4739">
                      <span className="text-slate-600" data-id="element-4740">Subject:</span>
                      <span className="font-medium text-slate-900 truncate max-w-[200px]" data-id="element-4741">
                        {emailSubject}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-4742">
                      <span className="text-slate-600" data-id="element-4743">Attachment:</span>
                      <span className="font-medium text-slate-900" data-id="element-4744">
                        PDF Summary
                      </span>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2" data-id="element-4745">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" data-id="element-4746" />
                    <p className="text-xs text-green-800" data-id="element-4747">
                      The customer will receive an email with their personalized
                      selection summary attached as a PDF.
                    </p>
                  </div>
                </div>
                <div className="p-6 border-t border-slate-200 flex justify-end" data-id="element-4748">
                  <Button onClick={handleCloseSendModal} data-id="element-4749">Done</Button>
                </div>
              </>}
          </div>
        </div>}
    </>;
}